/**
 * Sociétés de démonstration (SUPER_ADMIN).
 * Remise à neuf d'une société depuis son état de référence, ou nouvelle
 * référence figée depuis l'état actuel. Les deux actions demandent une
 * confirmation qui nomme la société.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, RotateCcw, Camera } from 'lucide-react';
import { demoAdminAPI, organizationsAPI } from '../lib/api';
import { toast } from 'sonner';
import { PageHeader } from '../components/ui/page-header';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { messageErreur } from '../lib/erreurs';
import { formatDateHeure, formatNombre } from '../lib/formats';

const ACTIONS = {
  restore: {
    titre: (nom) => `Remettre à neuf « ${nom} » ?`,
    description: (nom) =>
      `Toutes les données actuelles de « ${nom} » seront remplacées par la référence. Cette action est irréversible.`,
    bouton: 'Remettre à neuf',
    succes: 'Société remise à neuf',
    echec: 'Impossible de remettre cette société à neuf',
    appel: (id) => demoAdminAPI.restore(id),
  },
  capture: {
    titre: (nom) => `Figer l’état actuel de « ${nom} » ?`,
    description: (nom) =>
      `L’état actuel de « ${nom} » remplace la référence actuelle. L’ancienne référence est perdue.`,
    bouton: 'Figer comme référence',
    succes: 'Référence enregistrée',
    echec: 'Impossible de figer l’état de cette société',
    appel: (id) => demoAdminAPI.capture(id),
  },
};

const estIntrouvable = (erreur) => erreur?.response?.status === 404;

export default function DemonstrationPage() {
  const [societes, setSocietes] = useState([]);
  const [instantanes, setInstantanes] = useState({});
  const [loading, setLoading] = useState(true);
  const [demande, setDemande] = useState(null); // { action, societe }
  const [enCours, setEnCours] = useState(false);

  const chargerInstantane = useCallback(async (id) => {
    try {
      const instantane = await demoAdminAPI.getSnapshot(id);
      return { statut: 'ok', instantane };
    } catch (e) {
      return estIntrouvable(e) ? { statut: 'aucun' } : { statut: 'erreur' };
    }
  }, []);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const toutes = await organizationsAPI.getEvery();
      const demo = (Array.isArray(toutes) ? toutes : []).filter((o) => o?.is_demo);
      setSocietes(demo);
      const resultats = await Promise.all(demo.map((o) => chargerInstantane(o.id)));
      setInstantanes(Object.fromEntries(demo.map((o, i) => [o.id, resultats[i]])));
    } catch (e) {
      toast.error(messageErreur(e, 'Impossible de charger les sociétés de démonstration'));
    } finally {
      setLoading(false);
    }
  }, [chargerInstantane]);

  useEffect(() => {
    charger();
  }, [charger]);

  const confirmer = async (event) => {
    // Radix ferme la boîte au clic; on la garde ouverte jusqu'à la réponse.
    event.preventDefault();
    if (!demande) return;
    const { action, societe } = demande;
    const definition = ACTIONS[action];
    setEnCours(true);
    try {
      await definition.appel(societe.id);
      toast.success(definition.succes);
      setDemande(null);
      const resultat = await chargerInstantane(societe.id);
      setInstantanes((precedent) => ({ ...precedent, [societe.id]: resultat }));
    } catch (e) {
      toast.error(messageErreur(e, definition.echec));
    } finally {
      setEnCours(false);
    }
  };

  const stats = [{ label: 'Sociétés de démonstration', valeur: formatNombre(societes.length) }];
  const definition = demande ? ACTIONS[demande.action] : null;
  const nomDemande = demande?.societe?.name || 'cette société';

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Administration"
        title="Démonstration"
        description="Remettre à neuf une société de démonstration ou figer son état comme nouvelle référence."
        stats={stats}
        actions={
          <button onClick={charger} className="btn-secondary px-3" disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Rafraîchir
          </button>
        }
      />

      {!loading && societes.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground/70">
          Aucune société de démonstration.
        </div>
      ) : (
        <div className="space-y-3">
          {societes.map((societe) => {
            const etat = instantanes[societe.id];
            const instantane = etat?.statut === 'ok' ? etat.instantane : null;
            return (
              <div
                key={societe.id}
                data-testid="societe-demo"
                className="rounded-2xl border border-border bg-card p-4 shadow-sm"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="truncate text-sm font-bold text-foreground">{societe.name}</h2>
                    {instantane ? (
                      <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-xs md:grid-cols-4">
                        {[
                          ['Référence', instantane.label || '—'],
                          ['Figée le', formatDateHeure(instantane.captured_at)],
                          ['Remise à neuf le', formatDateHeure(instantane.restored_at)],
                          ['Dernière activité', formatDateHeure(instantane.last_activity_at)],
                          ['Tables', formatNombre(instantane.table_count)],
                          ['Lignes', formatNombre(instantane.row_count)],
                        ].map(([intitule, valeur]) => (
                          <div key={intitule}>
                            <dt className="text-muted-foreground">{intitule}</dt>
                            <dd className="font-medium tabular-nums">{valeur}</dd>
                          </div>
                        ))}
                      </dl>
                    ) : (
                      <div className="mt-1 text-xs text-muted-foreground">
                        {!etat ? '—' : etat.statut === 'aucun' ? 'Aucune référence' : 'Référence indisponible'}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      className="btn-secondary px-3"
                      onClick={() => setDemande({ action: 'capture', societe })}
                    >
                      <Camera className="h-4 w-4" aria-hidden="true" />
                      Figer l’état actuel comme référence
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                      disabled={etat?.statut !== 'ok'}
                      onClick={() => setDemande({ action: 'restore', societe })}
                    >
                      <RotateCcw className="h-4 w-4" aria-hidden="true" />
                      Remettre à neuf
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={Boolean(demande)} onOpenChange={(ouvert) => { if (!ouvert && !enCours) setDemande(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{definition?.titre(nomDemande)}</AlertDialogTitle>
            <AlertDialogDescription>{definition?.description(nomDemande)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={enCours}>Annuler</AlertDialogCancel>
            <AlertDialogAction disabled={enCours} onClick={confirmer}>
              {definition?.bouton}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
